import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { results as resultsCache } from "../local/db";

// #644 — placings/composite for ONE class. Same visibility-aware
// fetch-then-poll shape as ../hooks/useRideStatuses.js (which wraps
// common-fe's useRideStatuses), but keyed on a single classId rather than a
// set of ride ids, and reading GET /class/:classId/results — no new route.
//
// Two modes, chosen by the caller via `intervalMs`:
//   - omitted/falsy -> fetch once (for a class that's already wrapped; its
//     results won't change again once every ride is FINAL).
//   - set -> poll at that interval, pausing while the tab is hidden and doing
//     an immediate refresh on return, exactly like the ride-status poll.
// docs/score/announcer.md's freshness table: poll ONLY the current/live class.
export const RESULTS_POLL_MS = 15000;

export function useClassResults(
  classId,
  { intervalMs, enabled = true, online = true } = {},
) {
  const scoreApi = useScoreApi();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const classIdRef = useRef(classId);
  classIdRef.current = classId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  const onlineRef = useRef(online);
  onlineRef.current = online;

  const active = Boolean(enabled) && Boolean(classId);

  // #705 — read-through offline cache. Online: fetch, render, write the result
  // array VERBATIM into IndexedDB. Offline (or a failed fetch): fall back to the
  // last-cached array so placings/composites keep showing instead of vanishing.
  // We cache exactly what score-api returned, so a PARTIAL row still has no
  // number and the #697 reveal gate (keyed on each cached row's status) holds.
  const refresh = useCallback(async () => {
    const id = classIdRef.current;
    if (!id) return;
    setLoading(true);
    try {
      if (onlineRef.current) {
        const rows = unwrap(await apiRef.current.results.getClassResults(id)) || [];
        setResults(rows);
        setError(null);
        await resultsCache.put(id, rows);
      } else {
        const cached = await resultsCache.get(id);
        setResults(cached?.rows ?? []);
        setError(null);
      }
    } catch (err) {
      const cached = await resultsCache.get(id);
      if (cached) {
        setResults(cached.rows);
        setError(null);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) {
      setResults(null);
      return undefined;
    }

    // Fetch-once mode: no interval to arm, just get the latest and stop.
    if (!intervalMs) {
      refresh();
      return undefined;
    }

    // Polling mode: never fetch (not even the initial call) while the tab is
    // hidden. The visibilitychange handler below refreshes immediately the
    // moment it comes back, so nothing is stale on return.
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, intervalMs);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // classId (not just active) drives re-subscription so switching the live
    // class re-fetches immediately rather than waiting out the old interval.
    // `online` is a dep too so a connectivity flip re-runs refresh — offline
    // falls back to cache, back online re-fetches even for a fetch-once class.
  }, [classId, active, intervalMs, refresh, online]);

  return { results, loading, error, refresh };
}

export default useClassResults;
